/**
 * The global period, read from and written to the URL (docs/07-gui-proposal §2) — so a link
 * always opens the same screen for the next person. "Today" is taken once per mount from the
 * BUSINESS clock (Vietnam), never from the reader's own zone.
 *
 * Besides the one period every screen reads, the URL may carry a SET of whole years or months to
 * compare (việc 49, `utils/periodSet.ts`). Any single choice drops that set: picking "Hôm nay"
 * while three years are being compared means "stop comparing".
 */
import { businessToday } from '@thongketau/shared/vnTime';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { periodParams, type Preset, readPeriod, withPreset } from '../utils/period.js';
import {
  PERIODS_PARAM,
  periodSetParams,
  readPeriodSet,
  type SetKind,
  YTD_PARAM,
} from '../utils/periodSet.js';

const PERIOD_KEYS = ['preset', 'from', 'to', PERIODS_PARAM, YTD_PARAM];

export function useToday(): string {
  return useMemo(() => businessToday(new Date()), []);
}

export function usePeriod() {
  const [params, setParams] = useSearchParams();
  const today = useToday();
  const period = useMemo(() => readPeriod(params, today), [params, today]);
  const periodSet = useMemo(() => readPeriodSet(params, today), [params, today]);

  const write = useCallback(
    (values: Record<string, string>) => {
      setParams(
        (current) => {
          const merged = new URLSearchParams(current);
          for (const key of PERIOD_KEYS) merged.delete(key);
          for (const [key, value] of Object.entries(values)) merged.set(key, value);
          return merged;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const choosePreset = useCallback(
    (preset: Preset) => {
      write(periodParams(withPreset(period, preset, today)));
    },
    [period, today, write],
  );

  const chooseRange = useCallback(
    (from: string, to: string) => {
      write(periodParams({ preset: 'custom', from, to }));
    },
    [write],
  );

  /** Một hay nhiều trọn năm / trọn tháng; `ytd` cắt mọi năm ở cùng ngày của hôm nay. */
  const chooseSet = useCallback(
    (kind: SetKind, tokens: readonly string[], ytd = false) => {
      write(periodSetParams(kind, tokens, ytd, today));
    },
    [today, write],
  );

  return { period, periodSet, today, choosePreset, chooseRange, chooseSet };
}
